import { Check, X, Eye, RefreshCcw, Download } from "lucide-react";
import { API } from "@/lib/auth";
import { StatusPill, formatRelativeTime } from "./shared";

const CoupleRow = ({ row, onStatus, onView, onConsultation, onDelete, mode }) => {
  const offered = row.consultation_offered || "";
  const date = row.consultation_date || "";

  return (
    <tr
      data-testid={`couple-row-${row.id}`}
      className="border-b border-[var(--ld-border)] hover:bg-[var(--ld-ivory-warm)]/40 transition-colors"
    >
      <td className="py-4 pr-4">
        <div className="font-serif-display text-lg text-[var(--ld-ink)]">
          {row.couple_full_name}
        </div>
        <div className="text-xs text-[var(--ld-ink-muted)] mt-0.5">
          {row.email} · {row.mobile_number}
        </div>
      </td>
      <td className="py-4 pr-4 text-sm text-[var(--ld-ink-muted)]">
        {row.wedding_date}
        <div className="text-xs">{row.guest_count} guests</div>
      </td>
      <td className="py-4 pr-4 text-sm text-[var(--ld-ink-muted)]">
        <div className="flex flex-wrap gap-1.5">
          {(row.services_needed || []).slice(0, 3).map((s) => (
            <span
              key={s}
              className="px-2 py-0.5 text-[10px] tracking-[0.15em] uppercase border border-[var(--ld-ink)]/20"
            >
              {s}
            </span>
          ))}
          {(row.services_needed || []).length > 3 && (
            <span className="text-[10px] text-[var(--ld-ink-muted)]">
              +{row.services_needed.length - 3}
            </span>
          )}
        </div>
      </td>
      <td className="py-4 pr-4">
        <span
          data-testid={`couple-last-signin-${row.id}`}
          className={
            "text-[11px] tracking-[0.08em] " +
            (row.last_signin_at
              ? "text-[var(--ld-ink-muted)]"
              : "text-[var(--ld-ink-muted)]/55 italic")
          }
          title={
            row.last_signin_at
              ? new Date(row.last_signin_at).toLocaleString()
              : "Account has never signed in"
          }
        >
          {formatRelativeTime(row.last_signin_at)}
        </span>
      </td>
      <td className="py-4 pr-4">
        <StatusPill status={row.status} />
      </td>
      <td className="py-4 pr-4">
        <div className="flex items-center gap-2">
          <select
            data-testid={`couple-consult-offered-${row.id}`}
            value={offered}
            onChange={(e) =>
              onConsultation(row, {
                consultation_offered: e.target.value || null,
                consultation_date: date || null,
              })
            }
            className="h-8 px-2 text-[11px] tracking-[0.15em] uppercase bg-transparent border border-[var(--ld-ink)]/25 text-[var(--ld-ink)] focus:outline-none focus:border-[var(--ld-ink)]"
          >
            <option value="">—</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
          {offered === "yes" && (
            <input
              type="date"
              data-testid={`couple-consult-date-${row.id}`}
              value={date}
              onChange={(e) =>
                onConsultation(row, {
                  consultation_offered: "yes",
                  consultation_date: e.target.value || null,
                })
              }
              className="h-8 px-2 text-[11px] bg-transparent border border-[var(--ld-ink)]/25 text-[var(--ld-ink)] focus:outline-none focus:border-[var(--ld-ink)]"
            />
          )}
        </div>
      </td>
      <td className="py-4 pr-2 text-right">
        <div className="inline-flex items-center gap-1.5">
          <button
            data-testid={`couple-view-${row.id}`}
            onClick={() => onView(row)}
            title="View details"
            className="inline-flex h-8 w-8 items-center justify-center border border-[var(--ld-ink)]/30 text-[var(--ld-ink)] hover:bg-[var(--ld-ink)] hover:text-[var(--ld-gold)] transition-colors"
          >
            <Eye size={13} strokeWidth={1.6} />
          </button>
          {mode === "approved" && (
            <a
              data-testid={`couple-download-${row.id}`}
              href={`${API}/admin/couple-signups/${row.id}/download`}
              download
              title="Download completed form (PDF)"
              className="inline-flex h-8 px-3 items-center justify-center gap-1 text-[10px] tracking-[0.18em] uppercase border border-[var(--ld-gold-deep)]/40 text-[var(--ld-gold-deep)] hover:bg-[var(--ld-gold-deep)] hover:text-white transition-colors"
            >
              <Download size={11} strokeWidth={1.8} />
              Download
            </a>
          )}
          {mode !== "rejected" && (
            <>
              <button
                data-testid={`couple-approve-${row.id}`}
                onClick={() => onStatus(row, "approved")}
                disabled={row.status === "approved"}
                title="Approve"
                className="inline-flex h-8 px-3 items-center justify-center gap-1 text-[10px] tracking-[0.18em] uppercase bg-[var(--ld-ink)] text-[var(--ld-gold)] border border-[var(--ld-ink)] hover:bg-black hover:text-[#e9d59a] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <Check size={12} strokeWidth={1.8} />
                Approve
              </button>
              <button
                data-testid={`couple-reject-${row.id}`}
                onClick={() => onStatus(row, "rejected")}
                disabled={row.status === "rejected"}
                title="Reject"
                className="inline-flex h-8 px-3 items-center justify-center gap-1 text-[10px] tracking-[0.18em] uppercase bg-transparent text-[var(--ld-ink)] border border-[var(--ld-ink)]/40 hover:bg-[var(--ld-ink)] hover:text-[var(--ld-gold)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <X size={12} strokeWidth={1.8} />
                Reject
              </button>
            </>
          )}
          {mode === "rejected" && (
            <button
              data-testid={`couple-restore-${row.id}`}
              onClick={() => onStatus(row, "pending")}
              title="Restore to pending"
              className="inline-flex h-8 px-3 items-center justify-center gap-1 text-[10px] tracking-[0.18em] uppercase bg-transparent text-[var(--ld-ink)] border border-[var(--ld-ink)]/40 hover:bg-[var(--ld-ink)] hover:text-[var(--ld-gold)] transition-colors"
            >
              <RefreshCcw size={12} strokeWidth={1.8} />
              Restore
            </button>
          )}
          {onDelete && (
            <button
              data-testid={`couple-delete-${row.id}`}
              onClick={() => onDelete(row)}
              title="Permanently delete this sign-up"
              className="inline-flex h-8 px-3 items-center justify-center gap-1 text-[10px] tracking-[0.18em] uppercase bg-transparent text-red-700 border border-red-700/40 hover:bg-red-700/5 transition-colors"
            >
              <X size={12} strokeWidth={1.8} />
              Delete
            </button>
          )}
        </div>
      </td>
    </tr>
  );
};

export default CoupleRow;
